import React, { useState } from 'react';
import { Box, Group, Text, ActionIcon } from '@mantine/core';
import { IoChatbubbleOutline } from 'react-icons/io5';
import type { PostCardLinkComponentProps, PostCardProps } from './types';
import { PostTypeBadge } from './PostTypeBadge';
import { postRelativeDate } from './postRelativeDate';

const BTN_STYLE = {
  background: 'var(--gs-surface2)',
  border: '1px solid var(--gs-border-bright)',
  color: 'var(--gs-text)',
  padding: '2px 7px',
  borderRadius: 3,
  fontSize: 10
};

function DefaultLink ({ href, children, onClick, sx }: PostCardLinkComponentProps) {
  return (
    <Box
      component="a"
      href={href}
      onClick={onClick}
      sx={{
        textDecoration: 'none',
        color: 'inherit',
        '&:visited': { color: 'inherit' },
        ...sx
      }}
    >
      {children}
    </Box>
  );
}

function getInitials (displayName?: string | null, username?: string) {
  if (displayName?.trim()) {
    const parts = displayName.trim().split(/\s+/);
    if (parts.length >= 2) return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    return displayName.slice(0, 2).toUpperCase();
  }
  if (username?.trim()) return username.trim().slice(0, 2).toUpperCase();
  return '?';
}

/**
 * Presentational blog post list card. No routing, stores, or API — use GunSlop wrapper for those.
 */
export default function PostCard ({
  title,
  excerpt,
  type,
  coverImageUrl,
  mainPhotoUrl,
  authorUsername,
  authorDisplayName,
  authorImage = null,
  createdAt,
  tags,
  rating = null,
  postHref,
  commentsHref,
  commentCount = 0,
  voteScore,
  currentUserVote = null,
  onVote,
  authorBadge,
  actionsMenu,
  showActionsMenu = false,
  LinkComponent = DefaultLink
}: PostCardProps) {
  const [hovered, setHovered] = useState(false);
  const displayAuthorName = authorDisplayName?.trim() || authorUsername?.trim() || 'Member';
  const thumbUrl = mainPhotoUrl || coverImageUrl;

  const visibleTags = tags?.filter((t) => t.status === 1).slice(0, 3) ?? [];
  const hiddenTagCount = (tags?.filter((t) => t.status === 1).length ?? 0) - visibleTags.length;

  return (
    <Box
      sx={{ position: 'relative' }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      {showActionsMenu && actionsMenu && (
        <Box
          sx={{
            position: 'absolute',
            top: 8,
            right: 8,
            zIndex: 3,
            opacity: hovered ? 1 : 0,
            transition: 'opacity 120ms',
            pointerEvents: hovered ? 'auto' : 'none'
          }}
          onClick={(e) => e.preventDefault()}
        >
          {actionsMenu}
        </Box>
      )}

      <LinkComponent
        href={postHref}
        sx={{
          background: 'var(--gs-surface)',
          border: '1px solid var(--gs-border)',
          borderRadius: 8,
          display: 'flex',
          overflow: 'hidden',
          cursor: 'pointer',
          transition: 'border-color 150ms',
          textDecoration: 'none',
          color: 'var(--gs-text)',
          '&:visited': { color: 'var(--gs-text)' },
          '&:hover': { borderColor: 'var(--gs-accent)' }
        }}
      >
        <Box
          sx={{
            width: 88,
            minWidth: 88,
            background: thumbUrl
              ? `url(${thumbUrl}) center/cover`
              : 'linear-gradient(135deg, var(--gs-surface2), var(--gs-surface))'
          }}
        />

        <Box sx={{ flex: 1, padding: '10px 12px', minWidth: 0, display: 'flex', flexDirection: 'column', gap: 4 }}>
          <Group spacing={6} sx={{ alignItems: 'center' }}>
            <PostTypeBadge type={type} />
            {type === 1 && rating != null && (
              <span
                style={{
                  fontFamily: 'DM Mono, ui-monospace, monospace',
                  fontSize: 10,
                  fontWeight: 700,
                  color: 'var(--gs-gold)',
                  letterSpacing: 0
                }}
              >
                {'★'.repeat(rating)}
                {'☆'.repeat(5 - rating)}
              </span>
            )}
            <Text sx={{ fontFamily: 'DM Sans, sans-serif', fontSize: 10, color: 'var(--gs-muted)' }}>
              {postRelativeDate(createdAt)}
            </Text>
          </Group>

          <Text
            weight={600}
            size="sm"
            sx={{
              fontFamily: 'DM Sans, sans-serif',
              fontSize: 13,
              lineHeight: 1.35,
              textDecoration: 'underline',
              textDecorationColor: 'var(--gs-text)',
              color: 'var(--gs-text)'
            }}
            lineClamp={1}
          >
            {title}
          </Text>

          {excerpt && (
            <Text size="xs" sx={{ fontFamily: 'DM Sans, sans-serif', fontSize: 11, color: 'var(--gs-muted)' }} lineClamp={2}>
              {excerpt}
            </Text>
          )}

          {visibleTags.length > 0 && (
            <Group spacing={4} sx={{ marginTop: 2 }}>
              {visibleTags.map((tag) => (
                <Box
                  key={tag.id}
                  sx={{
                    background: 'rgba(88,166,255,0.1)',
                    border: '1px solid rgba(88,166,255,0.2)',
                    color: 'var(--gs-blue)',
                    padding: '1px 6px',
                    borderRadius: 3,
                    fontSize: 9,
                    fontWeight: 600,
                    fontFamily: 'DM Sans, sans-serif',
                    textTransform: 'uppercase',
                    letterSpacing: '0.3px'
                  }}
                >
                  {tag.displayName || tag.name}
                </Box>
              ))}
              {hiddenTagCount > 0 && (
                <Text sx={{ fontSize: 9, color: 'var(--gs-muted)', fontFamily: 'DM Sans, sans-serif' }}>
                  +{hiddenTagCount}
                </Text>
              )}
            </Group>
          )}

          <Group position="apart" spacing="xs" sx={{ marginTop: 'auto' }}>
            <Group spacing={6}>
              <Box
                sx={{
                  width: 18,
                  height: 18,
                  borderRadius: '50%',
                  overflow: 'hidden',
                  flexShrink: 0,
                  background: authorImage ? `url(${authorImage}) center/cover` : 'var(--gs-surface2)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: 9,
                  fontWeight: 600,
                  color: 'var(--gs-text)'
                }}
              >
                {!authorImage && getInitials(authorDisplayName, authorUsername)}
              </Box>
              <Text size="xs" sx={{ color: 'var(--gs-text)' }} lineClamp={1}>
                {displayAuthorName}
              </Text>
              {authorBadge}
            </Group>

            <Group spacing={6} data-vote-btn onClick={(e) => e.preventDefault()}>
              <ActionIcon
                size="sm"
                variant="subtle"
                sx={{
                  ...BTN_STYLE,
                  minWidth: 26,
                  ...(currentUserVote === 1 ? { borderColor: 'var(--gs-accent)', color: 'var(--gs-accent)' } : {})
                }}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  onVote(1);
                }}
                aria-label="Upvote"
              >
                <Text weight={800} size="xs" sx={{ lineHeight: 1 }}>
                  +
                </Text>
              </ActionIcon>
              <Text size="xs" sx={{ minWidth: 16, textAlign: 'center', fontSize: 10, fontWeight: 600 }}>
                {voteScore}
              </Text>
              <ActionIcon
                size="sm"
                variant="subtle"
                sx={{
                  ...BTN_STYLE,
                  minWidth: 26,
                  ...(currentUserVote === -1 ? { borderColor: 'var(--gs-accent)', color: 'var(--gs-accent)' } : {})
                }}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  onVote(-1);
                }}
                aria-label="Downvote"
              >
                <Text weight={800} size="xs" sx={{ lineHeight: 1 }}>
                  −
                </Text>
              </ActionIcon>
              <LinkComponent
                href={commentsHref}
                onClick={(e) => e.stopPropagation()}
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 4,
                  marginLeft: 4,
                  padding: '2px 8px',
                  borderRadius: 4,
                  border: '1px solid var(--gs-border)',
                  background: 'var(--gs-surface2)',
                  color: 'var(--gs-accent)',
                  fontSize: 10,
                  fontWeight: 600,
                  fontFamily: 'DM Sans, sans-serif',
                  textDecoration: 'underline',
                  textDecorationColor: 'var(--gs-accent)',
                  '&:visited': { color: 'var(--gs-accent)' },
                  '&:hover': { borderColor: 'var(--gs-accent)' }
                }}
              >
                <IoChatbubbleOutline size={12} />
                {commentCount > 0 ? commentCount : 'Comments'}
              </LinkComponent>
            </Group>
          </Group>
        </Box>
      </LinkComponent>
    </Box>
  );
}
